import React from 'react';
import { Code2, BarChart3, ShoppingCart, Layers, Target, Code, TrendingUp } from 'lucide-react';

const coreCapabilities = [
  {
    icon: <Code2 className="text-white mb-2" size={32} />,
    title: 'Complex Web Platforms',
    desc: 'Custom websites and platforms designed to support advanced logic, integrations, and scalable architectures.'
  },
  {
    icon: <BarChart3 className="text-white mb-2" size={32} />,
    title: 'Dashboards & Internal Systems',
    desc: 'Secure, data-driven dashboards and administrative tools built for operational efficiency and real-world use.'
  },
  {
    icon: <ShoppingCart className="text-white mb-2" size={32} />,
    title: 'E-Commerce Solutions',
    desc: 'Scalable e-commerce platforms with custom workflows, payment integrations, and performance-focused infrastructure.'
  },
];

const ourApproach = [
  {
    icon: <Layers className="text-white mb-2" size={32} />,
    title: 'Architecture-First',
    desc: 'Strong technical foundations designed to support growth and change.'
  },
  {
    icon: <Target className="text-white mb-2" size={32} />,
    title: 'Purpose-Driven Design',
    desc: 'Clean, intuitive interfaces aligned with business objectives.'
  },
  {
    icon: <Code className="text-white mb-2" size={32} />,
    title: 'Engineering Excellence',
    desc: 'Modern frameworks, best practices, and disciplined development processes.'
  },
  {
    icon: <TrendingUp className="text-white mb-2" size={32} />,
    title: 'Scalable Delivery',
    desc: 'Solutions built to evolve as your business grows.'
  },
];

const whyWorkWithUs = [
  'Focus on complex, high-impact systems',
  'Strong emphasis on performance and maintainability',
  'Clear processes and technical accountability',
  'Solutions designed for long-term value'
];

const clients = [
  { name: 'donziartacademy.com', url: 'https://donziartacademy.com', logo: require('./assets/donzi_logo.jpg'), alt: 'Donzi Art Academy Logo' },
  { name: 'x-maths.com', url: 'https://x-maths.com', logo: require('./assets/Xmaths.png'), alt: 'X-Maths Logo' },
];

export default function HeroAndSections() {
  return (
    <>
      {/* Modern Hero Section */}
      <section className="relative min-h-[85vh] flex items-center justify-center overflow-hidden py-20 lg:py-8">
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="max-w-4xl mx-auto">
            <div className="inline-block mb-6 px-4 py-2 rounded-full text-white text-sm font-medium"
                style={{
                  background: 'rgba(255, 255, 255, 0.12)',
                  backdropFilter: 'blur(16px)',
                  WebkitBackdropFilter: 'blur(16px)',
                  border: '1px solid rgba(255, 255, 255, 0.25)',
                }}
              >
                Next-generation web and software solutions
            </div>
            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-extrabold mb-4 sm:mb-6 text-white tracking-tight leading-tight px-2">
              Accelerate Growth.<br className="hidden sm:block" /> <span className="sm:hidden"> </span>Deliver Digital Excellence.
            </h1>
            <p className="text-lg sm:text-xl md:text-2xl lg:text-3xl text-white/90 mb-8 sm:mb-12 max-w-4xl mx-auto leading-relaxed font-medium px-2">
              Advanced web platforms, dashboards, and e-commerce systems built with precision, performance, and scalability in mind.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <a 
                href="#contact" 
                className="px-6 sm:px-8 py-3 sm:py-4 rounded-full font-semibold text-base sm:text-lg text-white transition-all duration-300 hover:scale-105"
                style={{
                  background: 'rgba(255, 255, 255, 0.18)',
                  backdropFilter: 'blur(16px)',
                  WebkitBackdropFilter: 'blur(16px)',
                  border: '1px solid rgba(255, 255, 255, 0.25)',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.25)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.18)';
                }}
              >
                Start a project
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Positioning Statement */}
      <section className="relative max-w-5xl mx-auto py-16 sm:py-24 px-4 sm:px-6 lg:px-8">
        <div className="relative z-10">
          <div 
            className="rounded-2xl p-6 sm:p-8 md:p-12 text-center"
            style={{
              background: 'rgba(255, 255, 255, 0.12)',
              backdropFilter: 'blur(16px)',
              WebkitBackdropFilter: 'blur(16px)',
              border: '1px solid rgba(255, 255, 255, 0.25)',
              boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.1)',
            }}
          >
            <p className="text-lg sm:text-xl md:text-2xl text-white/90 mb-4 sm:mb-6 leading-relaxed font-medium px-2">
              We design and develop robust digital platforms for businesses that require more than standard websites.
            </p>
            <p className="text-base sm:text-lg md:text-xl text-white/80 leading-relaxed px-2">
              Our work focuses on complex systems, data-driven applications, and high-performance e-commerce — delivered with modern engineering standards and long-term reliability.
            </p>
          </div>
        </div>
      </section>

      {/* Core Capabilities Section */}
      <section id="services" className="relative max-w-7xl mx-auto py-16 sm:py-24 px-4 sm:px-6 lg:px-8">
        <div className="relative z-10">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-white mb-4 tracking-tight">Core Capabilities</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {coreCapabilities.map((s, i) => (
              <div 
                key={i} 
                className="group relative rounded-2xl p-5 sm:p-6 md:p-8 flex flex-col items-center text-center transition-all duration-300 hover:-translate-y-1"
                style={{
                  background: 'rgba(255, 255, 255, 0.12)',
                  backdropFilter: 'blur(16px)',
                  WebkitBackdropFilter: 'blur(16px)',
                  border: '1px solid rgba(255, 255, 255, 0.25)',
                  boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.1)',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.18)';
                  e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.35)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.12)';
                  e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.25)';
                }}
              >
                <div className="mb-3 sm:mb-4 transform group-hover:scale-110 transition-transform duration-300">
                  <div className="scale-90 sm:scale-100">
                    {s.icon}
                  </div>
                </div>
                <div className="font-semibold text-base sm:text-lg md:text-xl text-white mb-2 sm:mb-3">{s.title}</div>
                <div className="text-white/80 text-xs sm:text-sm md:text-base leading-relaxed">{s.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Approach Section */}
      <section className="relative max-w-7xl mx-auto py-16 sm:py-24 px-2 sm:px-4">
        <div className="relative z-10">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-white mb-4 tracking-tight">Our Approach</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {ourApproach.map((item, i) => (
              <div 
                key={i} 
                className="group relative rounded-2xl p-6 sm:p-8 flex flex-col items-center text-center transition-all duration-300 hover:-translate-y-1"
                style={{
                  background: 'rgba(255, 255, 255, 0.12)',
                  backdropFilter: 'blur(16px)',
                  WebkitBackdropFilter: 'blur(16px)',
                  border: '1px solid rgba(255, 255, 255, 0.25)',
                  boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.1)',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.18)';
                  e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.35)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.12)';
                  e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.25)';
                }}
              >
                <div className="mb-3 sm:mb-4 transform group-hover:scale-110 transition-transform duration-300">
                  <div className="scale-90 sm:scale-100">
                    {item.icon}
                  </div>
                </div>
                <div className="font-semibold text-lg sm:text-xl md:text-2xl text-white mb-2 sm:mb-3">{item.title}</div>
                <div className="text-white/80 text-sm sm:text-base md:text-lg leading-relaxed">{item.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Work With Us Section */}
      <section id="whyus" className="relative max-w-6xl mx-auto py-16 sm:py-24 px-4 sm:px-6 lg:px-8">
        <div className="relative z-10">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-white mb-4 tracking-tight">Why Work With Us</h2>
          </div>
          <div 
            className="rounded-2xl p-6 sm:p-8 md:p-12 mb-8"
            style={{
              background: 'rgba(255, 255, 255, 0.12)',
              backdropFilter: 'blur(16px)',
              WebkitBackdropFilter: 'blur(16px)',
              border: '1px solid rgba(255, 255, 255, 0.25)',
              boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.1)',
            }}
          >
            <ul className="space-y-3 sm:space-y-4 text-left max-w-3xl mx-auto px-2">
              {whyWorkWithUs.map((item, i) => (
                <li key={i} className="flex items-start gap-2 sm:gap-3 text-white/90 text-sm sm:text-base md:text-lg">
                  <span className="text-white mt-1 flex-shrink-0">•</span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
            <p className="text-center text-white/80 text-base sm:text-lg md:text-xl mt-6 sm:mt-8 italic px-2">
              We treat digital products as strategic assets, not short-term deliverables.
            </p>
          </div>
        </div>
      </section>

      {/* Clients Section */}
      <section id="clients" className="relative max-w-6xl mx-auto py-16 sm:py-24 px-4 sm:px-6 lg:px-8">
        <div className="relative z-10">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-white mb-4 tracking-tight">Trusted By Our Clients</h2>
            <p className="text-lg text-white/80 max-w-2xl mx-auto">
              Partnering with innovative companies to achieve their goals
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 md:gap-8 lg:gap-12">
            {clients.map((c, i) => (
              <a
                key={i}
                href={c.url}
                target="_blank"
                rel="noopener noreferrer"
                className="group relative rounded-2xl p-5 sm:p-6 md:p-8 transition-all duration-300 hover:-translate-y-1 flex flex-col items-center text-center cursor-pointer no-underline"
                style={{
                  background: 'rgba(255, 255, 255, 0.12)',
                  backdropFilter: 'blur(16px)',
                  WebkitBackdropFilter: 'blur(16px)',
                  border: '1px solid rgba(255, 255, 255, 0.25)',
                  boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.1)',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.18)';
                  e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.35)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.12)';
                  e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.25)';
                }}
              >
                {c.name === 'donziartacademy.com' && (
                  <div className="mb-4 sm:mb-6 transform group-hover:scale-105 transition-transform duration-300">
                    <img src={c.logo} alt={c.alt} className="h-16 sm:h-20 max-w-xs w-full object-contain drop-shadow-lg bg-white p-3 sm:p-4 rounded-xl" />
                  </div>
                )}
                {c.name === 'x-maths.com' && (
                  <div className="mb-4 sm:mb-6 transform group-hover:scale-105 transition-transform duration-300">
                    <img src={c.logo} alt={c.alt} className="h-16 sm:h-20 max-w-xs w-full object-contain drop-shadow-lg bg-white p-3 sm:p-4 rounded-xl brightness-200" />
                  </div>
                )}
                <div className="font-semibold text-sm sm:text-base md:text-lg lg:text-xl text-white break-all px-2">{c.name}</div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Call to Action Section */}
      <section className="relative max-w-5xl mx-auto py-16 sm:py-24 px-4 sm:px-6 lg:px-8">
        <div className="relative z-10">
          <div 
            className="rounded-2xl p-6 sm:p-8 md:p-12 text-center w-full"
            style={{
              background: 'rgba(255, 255, 255, 0.12)',
              backdropFilter: 'blur(16px)',
              WebkitBackdropFilter: 'blur(16px)',
              border: '1px solid rgba(255, 255, 255, 0.25)',
              boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.1)',
            }}
          >
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-extrabold text-white mb-4 sm:mb-6 tracking-tight px-2">Build with confidence</h2>
            <p className="text-base sm:text-lg md:text-xl text-white/80 mb-6 sm:mb-8 max-w-3xl mx-auto leading-relaxed px-2">
              If your organization needs a reliable partner for complex web platforms, dashboards, or e-commerce systems, we're ready to help.
            </p>
            <a 
              href="#contact" 
              className="inline-block px-6 sm:px-8 py-3 sm:py-4 rounded-full font-semibold text-base sm:text-lg text-white transition-all duration-300 hover:scale-105"
              style={{
                background: 'rgba(255, 255, 255, 0.18)',
                backdropFilter: 'blur(16px)',
                WebkitBackdropFilter: 'blur(16px)',
                border: '1px solid rgba(255, 255, 255, 0.25)',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.25)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.18)';
              }}
            >
              Contact us
            </a>
          </div>
        </div>
      </section>

      {/* Contact CTA Section */}
      <section id="contact" className="relative max-w-3xl mx-auto pt-16 sm:pt-24 pb-0 px-4 sm:px-6 lg:px-8">
        <div className="relative z-10">
          <div 
            className="rounded-2xl p-6 sm:p-8 md:p-12 text-center"
            style={{
              background: 'rgba(255, 255, 255, 0.12)',
              backdropFilter: 'blur(16px)',
              WebkitBackdropFilter: 'blur(16px)',
              border: '1px solid rgba(255, 255, 255, 0.25)',
              boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.1)',
            }}
          >
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-extrabold text-white mb-4 sm:mb-6 tracking-tight px-2">Get In Touch</h2>
            <p className="text-base sm:text-lg md:text-xl text-white/80 mb-6 sm:mb-8 max-w-2xl mx-auto leading-relaxed px-2">
              Ready to start your next project or have a question? Contact us and our team will get back to you within one business day.
            </p>
            <a 
              href="/contact" 
              className="inline-block px-6 sm:px-8 py-3 sm:py-4 rounded-full font-semibold text-base sm:text-lg text-white transition-all duration-300 hover:scale-105"
              style={{
                background: 'rgba(255, 255, 255, 0.18)',
                backdropFilter: 'blur(16px)',
                WebkitBackdropFilter: 'blur(16px)',
                border: '1px solid rgba(255, 255, 255, 0.25)',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.25)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.18)';
              }}
            >
              Contact us
            </a>
          </div>
        </div>
      </section>
    </>
  );
} 